import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { Category, Product } from '@/services/catalog.service';
import { useAuthStore } from '@/stores/auth.store';
import { pickLocalized } from '@/i18n';
import { haptic } from '@/shared/lib/telegram';
import { formatUZS } from '@/shared/lib/format';
import { CategoryIcon } from './CategoryIcon';

const LOCKED_LABEL: Record<string, string> = {
  uz: 'Cheklangan',
  ru: 'Ограничено',
  en: 'Restricted',
};

const GRADIENT: Record<string, string> = {
  instagram_verification:
    'from-[#3897F0] via-[#1F7CDC] to-[#0058B8]',
  telegram_premium: 'from-[#A78BFA] via-[#7C3AED] to-[#5B21B6]',
  telegram_stars: 'from-[#FFB200] via-[#FF8A00] to-[#FF6600]',
  telegram_gifts: 'from-[#FF6B9D] via-[#D4537E] to-[#A23062]',
  telegram_nft: 'from-[#C084FC] via-[#8B5CF6] to-[#4C1D95]',
  telegram_verification: 'from-[#5AC8FA] via-[#2AABEE] to-[#0A6BB5]',
};

const HINT: Record<string, { uz: string; ru: string; en: string }> = {
  instagram_verification: {
    uz: '1 oydan boshlab',
    ru: 'от 1 месяца',
    en: 'from 1 month',
  },
  telegram_premium: {
    uz: '1–12 oy',
    ru: '1–12 месяцев',
    en: '1–12 months',
  },
  telegram_stars: {
    uz: '100–150 000 ⭐',
    ru: '100–150 000 ⭐',
    en: '100–150K ⭐',
  },
  telegram_gifts: {
    uz: 'Maxsus sovg‘alar',
    ru: 'Особые подарки',
    en: 'Special gifts',
  },
  telegram_nft: {
    uz: 'Kolleksion NFT',
    ru: 'Коллекционные NFT',
    en: 'Collectible NFTs',
  },
  telegram_verification: {
    uz: 'Kanal yoki hisob',
    ru: 'Канал или аккаунт',
    en: 'Channel or account',
  },
};

export function CategoryCard({
  category,
  featured,
  restricted = false,
}: {
  category: Category;
  /** Subset of products used to compute the headline price + discount for this category. */
  featured?: Product[];
  /** Admin-banned this service for the current user → lock the card. */
  restricted?: boolean;
}) {
  const lang = useAuthStore((s) => s.language);
  const name = pickLocalized(category, 'name', lang);
  const gradient = GRADIENT[category.kind] ?? 'from-bg-surface to-bg-surface-2';

  const inCategory = (featured ?? []).filter((p) => p.categoryId === category.id);
  const maxDiscountPct = inCategory.reduce((max, p) => {
    if (!p.oldPrice) return max;
    const pct = Math.round((1 - Number(p.price) / Number(p.oldPrice)) * 100);
    return pct > max ? pct : max;
  }, 0);
  const minPrice = inCategory.reduce<number | null>(
    (min, p) => {
      const n = Number(p.price);
      return n > 0 && (min === null || n < min) ? n : min;
    },
    null,
  );

  const hint = HINT[category.kind]?.[lang as 'uz' | 'ru' | 'en'] ?? '';

  const cardClass = cn(
    'relative block overflow-hidden rounded-3xl p-4 h-44',
    'bg-gradient-to-br shadow-card ring-1 ring-white/10',
    gradient,
    restricted && 'grayscale-[0.7] brightness-[0.6]',
  );

  const inner = (
    <>
      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/15 blur-2xl pointer-events-none" />

      {maxDiscountPct >= 5 && !restricted && (
        <div className="absolute top-3 left-3 z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-white/30 blur-lg rounded-full" />
            <span className="relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-[11px] font-bold text-black shadow-lg">
              −{maxDiscountPct}%
            </span>
          </div>
        </div>
      )}

      <div className="absolute top-3 right-3">
        <CategoryIcon kind={category.kind} size={64} />
      </div>

      <div className="absolute left-4 bottom-3 right-4">
        <h3 className="text-white font-bold text-base leading-tight tracking-tight drop-shadow-sm">
          {name}
        </h3>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          {minPrice !== null ? (
            <div className="text-white/95 text-xs font-medium">
              <span className="opacity-75">
                {lang === 'ru' ? 'от' : lang === 'en' ? 'from' : 'narxi'}{' '}
              </span>
              <span className="font-bold">{formatUZS(minPrice)}</span>
            </div>
          ) : (
            <div className="text-white/85 text-xs font-medium">{hint}</div>
          )}
          <span className="text-white/85 text-base leading-none">›</span>
        </div>
      </div>

      {/* Locked overlay — admin banned this service for the user. */}
      {restricted && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/35 backdrop-blur-[2px]">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
            <Lock className="h-5 w-5 text-white" />
          </div>
          <span className="rounded-full bg-black/50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white ring-1 ring-white/20">
            {LOCKED_LABEL[lang] ?? LOCKED_LABEL.uz}
          </span>
        </div>
      )}
    </>
  );

  if (restricted) {
    return (
      <motion.div whileTap={{ scale: 0.98 }} className="relative">
        <button
          type="button"
          onClick={() => haptic('error')}
          className={cn(cardClass, 'w-full text-left cursor-not-allowed')}
        >
          {inner}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div whileTap={{ scale: 0.97 }} className="relative">
      <Link
        to={`/category/${category.slug}`}
        onClick={() => haptic('selection')}
        className={cardClass}
      >
        {inner}
      </Link>
    </motion.div>
  );
}
